import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useSWR from 'swr';

// material-ui
import useMediaQuery from '@mui/material/useMediaQuery';
import Avatar from '@mui/material/Avatar';
import Badge from '@mui/material/Badge';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';

// project imports
import MainCard from 'components/MainCard';
import IconButton from 'components/@extended/IconButton';
import Transitions from 'components/@extended/Transitions';
import { notificationService } from 'services/notificationService';

// assets
import BellOutlined from '@ant-design/icons/BellOutlined';
import CheckCircleOutlined from '@ant-design/icons/CheckCircleOutlined';
import ClockCircleOutlined from '@ant-design/icons/ClockCircleOutlined';
import ExclamationCircleOutlined from '@ant-design/icons/ExclamationCircleOutlined';

const KEY = 'notifications';
// Sem websocket: busca a cada minuto e ao voltar para a aba.
const POLL_INTERVAL_MS = 60000;

const KIND_STYLE = {
  overdue: { icon: ExclamationCircleOutlined, color: 'error.main', bgcolor: 'error.lighter' },
  due_soon: { icon: ClockCircleOutlined, color: 'warning.main', bgcolor: 'warning.lighter' }
};

const formatWhen = (iso) => {
  if (!iso) return '';
  const date = new Date(iso);
  return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
};

// ==============================|| HEADER CONTENT - NOTIFICATION ||============================== //

export default function Notification() {
  const downMD = useMediaQuery((theme) => theme.breakpoints.down('md'));
  const navigate = useNavigate();

  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);

  // Falha de rede não derruba o cabeçalho: o sino só fica sem contador.
  const { data, mutate } = useSWR(KEY, () => notificationService.list(), {
    refreshInterval: POLL_INTERVAL_MS,
    revalidateOnFocus: true,
    shouldRetryOnError: false
  });

  const items = data?.data ?? [];
  const unread = data?.unread_count ?? 0;

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  const handleOpenItem = async (item) => {
    setOpen(false);
    if (!item.read_at) {
      try {
        await notificationService.markAsRead(item.id);
      } finally {
        mutate();
      }
    }
    if (item.url) navigate(item.url);
  };

  const handleMarkAll = async () => {
    try {
      await notificationService.markAllAsRead();
    } finally {
      mutate();
    }
  };

  return (
    <Box sx={{ flexShrink: 0, ml: 0.75 }}>
      <IconButton
        color="secondary"
        variant="light"
        sx={(theme) => ({
          color: 'text.primary',
          bgcolor: open ? 'grey.100' : 'transparent',
          ...theme.applyStyles('dark', { bgcolor: open ? 'background.default' : 'transparent' })
        })}
        aria-label={unread > 0 ? `Notificações: ${unread} não lidas` : 'Notificações'}
        ref={anchorRef}
        aria-controls={open ? 'notification-grow' : undefined}
        aria-haspopup="true"
        onClick={handleToggle}
      >
        <Badge badgeContent={unread} color="primary" max={99}>
          <BellOutlined />
        </Badge>
      </IconButton>
      <Popper
        placement={downMD ? 'bottom' : 'bottom-end'}
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        popperOptions={{ modifiers: [{ name: 'offset', options: { offset: [downMD ? -5 : 0, 9] } }] }}
      >
        {({ TransitionProps }) => (
          <Transitions type="grow" position={downMD ? 'top' : 'top-right'} in={open} {...TransitionProps}>
            <Paper
              id="notification-grow"
              sx={(theme) => ({ boxShadow: theme.customShadows.z1, width: '100%', minWidth: 285, maxWidth: { xs: 285, md: 420 } })}
            >
              <ClickAwayListener onClickAway={handleClose}>
                <MainCard
                  title="Notificações"
                  elevation={0}
                  border={false}
                  content={false}
                  secondary={
                    unread > 0 && (
                      <Tooltip title="Marcar todas como lidas">
                        <IconButton color="success" size="small" onClick={handleMarkAll} aria-label="Marcar todas como lidas">
                          <CheckCircleOutlined style={{ fontSize: '1.15rem' }} />
                        </IconButton>
                      </Tooltip>
                    )
                  }
                >
                  {items.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ px: 2.5, py: 3, textAlign: 'center' }}>
                      Nenhuma notificação por aqui.
                    </Typography>
                  ) : (
                    <List component="nav" sx={{ p: 0, maxHeight: 400, overflowY: 'auto' }}>
                      {items.map((item) => {
                        const style = KIND_STYLE[item.kind] ?? KIND_STYLE.due_soon;
                        const Icon = style.icon;
                        return (
                          <ListItemButton
                            key={item.id}
                            divider
                            onClick={() => handleOpenItem(item)}
                            sx={{ py: 1, px: 2, alignItems: 'flex-start', bgcolor: item.read_at ? 'transparent' : 'action.hover' }}
                          >
                            <ListItemAvatar>
                              <Avatar sx={{ width: 36, height: 36, fontSize: '1rem', color: style.color, bgcolor: style.bgcolor }}>
                                <Icon />
                              </Avatar>
                            </ListItemAvatar>
                            <ListItemText
                              primary={
                                <Typography variant="subtitle1" sx={{ fontWeight: item.read_at ? 500 : 700 }}>
                                  {item.title}
                                </Typography>
                              }
                              secondary={item.message}
                            />
                            <Typography variant="caption" noWrap sx={{ ml: 1, mt: 0.5, color: 'text.secondary' }}>
                              {formatWhen(item.created_at)}
                            </Typography>
                          </ListItemButton>
                        );
                      })}
                    </List>
                  )}
                </MainCard>
              </ClickAwayListener>
            </Paper>
          </Transitions>
        )}
      </Popper>
    </Box>
  );
}
